import { useEffect, useState, createContext, ReactNode, FunctionComponent } from 'react';
import PropTypes from 'prop-types';
import { useCompanies, useRoles } from '@adsk/alloy-provider';
import { useContextWithValidation } from '@adsk/alloy-react-context';
import { useUsers } from './RFIsUsersProvider';
import buildOxygenObjects from '../helpers/oxygenHelpers';
import { OxygenUser, OxygenRole, OxygenCompany } from '../sdk/types';

export type Oxygen = {
  users: Record<OxygenUser['autodeskId'], OxygenUser>;
  companies: Record<OxygenCompany['id'], OxygenCompany>;
  roles: Record<OxygenRole['id'], OxygenRole>;
  loading: boolean;
};

export const OXYGEN_INITIAL_VALUE: Oxygen = {
  users: {},
  companies: {},
  roles: {},
  loading: true,
};

const OxygenContext = createContext(OXYGEN_INITIAL_VALUE);

const useOxygen = () => {
  const oxygen = useContextWithValidation(OxygenContext, {
    hook: 'useOxygen',
    provider: 'OxygenProvider',
  });

  return oxygen;
};

const OxygenProvider: FunctionComponent<ReactNode> = ({ children }) => {
  const [oxygen, setOxygen] = useState(OXYGEN_INITIAL_VALUE);
  const { data: users, loading: loadingUsers } = useUsers();
  const { data: companies, loading: loadingCompanies } = useCompanies();
  const { data: roles, loading: loadingRoles } = useRoles();

  useEffect(() => {
    const allFetched =
      ![loadingUsers, loadingCompanies, loadingRoles].some((loading) => loading) && !!users.length;

    if (allFetched) {
      const oxygenObjects = buildOxygenObjects({ users, companies, roles });
      setOxygen({ ...oxygenObjects, loading: false });
    }
  }, [users, companies, roles, loadingUsers, loadingCompanies, loadingRoles]);

  return <OxygenContext.Provider value={oxygen}>{children}</OxygenContext.Provider>;
};

OxygenProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export { OxygenProvider, OxygenContext, useOxygen };
export default OxygenProvider;
